import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import Logo from "@/components/Logo";
import LangSwitch from "@/components/LangSwitch";
import { House, DownloadSimple, CircleNotch } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

// Thumbnail filenames (backend serves them at /api/samples/thumb/{name}).
const EN_THUMBS = [
  { file: "en_1_cover.jpg",       label: "Cover" },
  { file: "en_2_ceo.jpg",         label: "CEO Message" },
  { file: "en_3_materiality.jpg", label: "Materiality Matrix" },
  { file: "en_3_energy.jpg",      label: "Energy Management" },
  { file: "en_4_workforce.jpg",   label: "Workforce Diversity" },
  { file: "en_5_community.jpg",   label: "Community & CSR" },
];
const AR_THUMBS = [
  { file: "ar_1_cover.jpg",       label: "الغلاف" },
  { file: "ar_2_ceo.jpg",         label: "رسالة الرئيس التنفيذي" },
  { file: "ar_3_materiality.jpg", label: "مصفوفة الأهمية النسبية" },
  { file: "ar_3_energy.jpg",      label: "إدارة الطاقة" },
  { file: "ar_4_workforce.jpg",   label: "تنوع القوى العاملة" },
  { file: "ar_5_community.jpg",   label: "المشاركة المجتمعية" },
];

export default function SamplePage() {
  const { t, isRtl } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ company_name: "", name: "", surname: "", email: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleDownload = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.company_name.trim() || !form.name.trim() || !form.surname.trim() || !form.email.trim()) {
      setError(isRtl ? "يرجى ملء جميع الحقول." : "Please fill in all fields.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError(isRtl ? "يرجى إدخال بريد إلكتروني صحيح." : "Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/samples/download`, form, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "ESG_Report_Builder_Samples.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (isRtl ? "تعذّر التنزيل. حاول مرة أخرى." : "Download failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const Thumb = ({ file, label }) => (
    <div className="group relative aspect-[3/4] rounded-lg overflow-hidden border border-white/10 bg-black/40 shadow-[0_8px_24px_rgba(0,0,0,0.4)] hover:border-[#01AF9B]/60 transition-all duration-300 md:hover:scale-[2.15] md:hover:z-30 md:hover:shadow-[0_20px_60px_rgba(0,0,0,0.75)] origin-center will-change-transform">
      <img
        src={`${API}/api/samples/thumb/${file}`}
        alt={label}
        className="w-full h-full object-cover object-top transition-transform duration-500"
        loading="lazy"
        data-testid={`sample-thumb-${file.replace('.jpg', '')}`}
      />
      <div className="absolute inset-x-0 bottom-0 px-3 py-2 bg-gradient-to-t from-black/85 to-transparent md:group-hover:opacity-0 transition-opacity duration-200">
        <div className="text-white text-xs font-medium tracking-wide truncate">{label}</div>
      </div>
    </div>
  );

  const inputClass = "w-full bg-[#0B0C10] border border-slate-700 text-slate-100 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-[#01AF9B] focus:border-transparent outline-none transition-all";
  const labelClass = "font-['Outfit'] text-xs font-semibold tracking-wider uppercase text-slate-400 mb-1.5 block";

  return (
    <div dir={isRtl ? "rtl" : "ltr"} className="min-h-screen" style={{ background: "linear-gradient(165deg, #070709 0%, #0A0D12 50%, #070709 100%)" }} data-testid="sample-page">
      <header className="sticky top-0 z-40 bg-black border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="sample-home-link"><Logo size="md" /></Link>
          <div className="flex items-center gap-3">
            <LangSwitch />
            <button onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 text-slate-300 hover:text-white font-['Outfit'] text-xs sm:text-sm font-medium px-3 sm:px-4 py-2 rounded-lg transition-all"
              data-testid="sample-back-home">
              <House size={16} /> <span className="hidden sm:inline">{isRtl ? "الرئيسية" : "Home"}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 sm:py-16">
        {/* Intro */}
        <div className="text-center mb-10 sm:mb-14 animate-fade-in-up">
          <div className="font-['Outfit'] text-xs font-semibold tracking-[0.2em] uppercase text-[#01AF9B] mb-3">
            {isRtl ? "تقرير عيّنة" : "Sample Report"}
          </div>
          <h1 className="font-['Outfit'] font-bold text-3xl sm:text-5xl lg:text-6xl text-white mb-4 leading-[1.05] tracking-tight">
            {isRtl ? "شاهد كيف يبدو التقرير الحقيقي" : "See What a Real Report Looks Like"}
          </h1>
          <p className="text-slate-400 text-base max-w-2xl mx-auto leading-relaxed">
            {isRtl
              ? "معاينة من التقرير الحقيقي بصفحاته الأصلية باللغتين الإنجليزية والعربية. املأ النموذج أدناه لتنزيل النسختين الكاملتين مجاناً."
              : "A preview from the real report in its original English and Arabic pages. Fill the form below to instantly download both full samples for free."}
          </p>
        </div>

        {/* Gallery — Row 1: EN (LTR), Row 2: AR (RTL) */}
        <section className="mb-12 space-y-6">
          <div dir="ltr">
            <div className="flex items-center gap-3 mb-3">
              <span className="font-['Outfit'] text-xs font-bold tracking-[0.2em] uppercase text-[#01AF9B]">English</span>
              <span className="h-[1px] flex-1 bg-gradient-to-r from-[#01AF9B]/40 to-transparent"></span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 sm:gap-4" data-testid="sample-thumbs-en">
              {EN_THUMBS.map(t => <Thumb key={t.file} {...t} />)}
            </div>
          </div>
          <div dir="rtl">
            <div className="flex items-center gap-3 mb-3">
              <span className="font-['Outfit'] text-xs font-bold tracking-[0.2em] uppercase text-[#B94B05]">العربية · Arabic</span>
              <span className="h-[1px] flex-1 bg-gradient-to-l from-[#B94B05]/40 to-transparent"></span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 sm:gap-4" data-testid="sample-thumbs-ar">
              {AR_THUMBS.map(t => <Thumb key={t.file} {...t} />)}
            </div>
          </div>
        </section>

        {/* Lead capture form */}
        <section className="max-w-2xl mx-auto bg-[#111216] border border-slate-800/60 rounded-2xl p-6 sm:p-8 shadow-[0_8px_32px_rgba(0,0,0,0.4)]" data-testid="sample-form">
          <h2 className="font-['Outfit'] text-xl sm:text-2xl font-bold text-white mb-2">
            {isRtl ? "حمّل تقريراً كاملاً كعيّنة" : "Download a Full Sample Report"}
          </h2>
          <p className="text-slate-400 text-sm mb-6">
            {isRtl ? "جميع الحقول إلزامية. سنرسل نسخة ZIP تحتوي على التقريرين EN + AR." : "All fields are required. You'll receive a ZIP containing both EN + AR reports."}
          </p>
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 text-red-400 text-sm" data-testid="sample-error">{error}</div>
          )}
          <form onSubmit={handleDownload} className="space-y-4" noValidate>
            <div>
              <label className={labelClass}>{isRtl ? "اسم الشركة *" : "Company Name *"}</label>
              <input required data-testid="sample-company" type="text" value={form.company_name} onChange={update("company_name")} className={inputClass} placeholder={isRtl ? "شركتك" : "Your company"} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>{isRtl ? "الاسم الأول *" : "First Name *"}</label>
                <input required data-testid="sample-name" type="text" value={form.name} onChange={update("name")} className={inputClass} placeholder={isRtl ? "الاسم" : "First name"} />
              </div>
              <div>
                <label className={labelClass}>{isRtl ? "اسم العائلة *" : "Surname *"}</label>
                <input required data-testid="sample-surname" type="text" value={form.surname} onChange={update("surname")} className={inputClass} placeholder={isRtl ? "اسم العائلة" : "Surname"} />
              </div>
            </div>
            <div>
              <label className={labelClass}>{isRtl ? "البريد الإلكتروني *" : "Email *"}</label>
              <input required data-testid="sample-email" type="email" value={form.email} onChange={update("email")} className={inputClass} placeholder="you@company.com" dir="ltr" />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="sample-submit"
              className="w-full bg-[#01AF9B] hover:bg-[#018F7E] text-white font-['Outfit'] font-semibold py-3.5 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
              {loading
                ? <><CircleNotch size={18} className="animate-spin" /> {isRtl ? "جارٍ التنزيل..." : "Preparing download..."}</>
                : <><DownloadSimple size={18} weight="bold" /> {isRtl ? "تنزيل التقرير الكامل" : "Download Full Report"}</>
              }
            </button>
            <p className="text-slate-500 text-xs text-center pt-1">
              {isRtl ? "بالضغط على التنزيل، توافق على أن نتواصل معك بخصوص المنصة." : "By clicking download, you agree we may contact you about the platform."}
            </p>
          </form>
        </section>
      </main>
    </div>
  );
}
